import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Button } from 'reactstrap';
import { music24PlanCapabilities, VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { useIsPhoneLayout } from '../phone/useIsPhoneLayout';
import { useRequiredMusicVideoShowcase } from './MusicVideoShowcaseContext';
import { showcaseMessages } from './showcaseMessages';
import { ShowcaseSignUpLink } from './ShowcaseSignUpLink';
import { SHOWCASE_UPSELL_LINES } from './showcaseUpsellLines';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the showcase parts; each part uses its own slice of it */
import styles from './MusicVideoShowcase.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The sign-up card of the showcase (design c2): one card for every action that the demo does not
 * run; only the purple line changes. "Keep playing", Escape and a click on the dim area close it,
 * and the edits stay. On a phone it is a bottom sheet (design c5).
 */
const ShowcaseUpsellDialogComponent: React.FC = () => {
  const intl = useIntl();
  const isPhone = useIsPhoneLayout();
  const { upsell, closeUpsell } = useRequiredMusicVideoShowcase();
  const keepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (upsell === null) {
      return undefined;
    }
    keepRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeUpsell();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [closeUpsell, upsell]);

  if (upsell === null) {
    return null;
  }
  const line = SHOWCASE_UPSELL_LINES[upsell];
  const values = { credits: VIDEO_CREDIT_COST.aiClip };
  return (
    // A click on the dim area closes the card; the keys close it through the listener above.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={clsx(styles.dim, isPhone && styles.dimSheet)}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeUpsell();
        }
      }}
    >
      <div
        className={clsx(styles.card, isPhone && styles.cardSheet)}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage(showcaseMessages.cardLabel)}
      >
        <span className={styles.chip}>{intl.formatMessage(showcaseMessages.demoChip)}</span>
        <h3>{withBrandWord(intl.formatMessage(showcaseMessages.cardTitle))}</h3>
        <div className={styles.why} data-action={upsell}>
          <b>{intl.formatMessage(line.lead, values)}</b>
          {intl.formatMessage(line.rest, values)}
        </div>
        <ul className={styles.ticks}>
          <li>{intl.formatMessage(showcaseMessages.tickSong)}</li>
          <li>{intl.formatMessage(showcaseMessages.tickFootage)}</li>
          <li>
            {intl.formatMessage(showcaseMessages.tickFile, {
              cost: VIDEO_CREDIT_COST.fullSong,
              credits: music24PlanCapabilities.pro.videoCreditsPerMonth,
            })}
          </li>
        </ul>
        <div className={styles.actions}>
          <Button innerRef={keepRef} color="secondary" outline size="sm" onClick={closeUpsell}>
            {intl.formatMessage(showcaseMessages.keepPlaying)}
          </Button>
          <ShowcaseSignUpLink place={upsell}>{intl.formatMessage(showcaseMessages.startFree)}</ShowcaseSignUpLink>
        </div>
        <span className={styles.fine}>{intl.formatMessage(showcaseMessages.fine)}</span>
      </div>
    </div>
  );
};

export const ShowcaseUpsellDialog = TypedMemo(ShowcaseUpsellDialogComponent);
